import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer } from 'redux-injectors';
import PropTypes from 'prop-types';

import { utagLinkPDP } from 'features/tealium';
import ProductNewAdditionAndSorting from 'features/productQuestionsAndAnswers/components/ProductNewAdditionAndSorting';
import ProductQuestionsList from 'features/productQuestionsAndAnswers/components/ProductQuestionsList';
import ProductQuestionsAndAnswersPaginationList from 'features/productQuestionsAndAnswers/components/ProductQuestionsAndAnswersPaginationList';
import ProductSearchUGCResults from 'features/productSearchUgc/components/ProductSearchUGCResults';
import { showMoreResults } from 'features/productSearchUgc/questionsAndAnswersInstanceSlice';
import { enableNewQuestionMode, resetState } from 'features/productQuestionsAndAnswers/slice';
import ProductQuestionsAndAnswersNewQuestion from 'features/productQuestionsAndAnswers/components/ProductQuestionsAndAnswersNewQuestion';
import ProductQuestionsAndAnswersSearchHint from 'features/productQuestionsAndAnswers/components/ProductQuestionsAndAnswersSearchHint';
import ProductQuestionsAndAnswersSearch from 'features/productSearchUgc/components/ProductQuestionsAndAnswersSearch';
import ProductQuestionsAndAnswersThankYou from 'features/productQuestionsAndAnswers/components/ProductQuestionsAndAnswersThankYou';
import { reducer as productQuestionsAndAnswers } from 'features/productQuestionsAndAnswers';
import { questionsAndAnswersSearchInstanceReducer } from 'features/productSearchUgc';

import './AppProductQuestionsAndAnswers.scss';

const isMobile = !!(window.utag_data && window.utag_data.is_mobile === '1');
const AppProductQuestionsAndAnswers = ({ resetInitialState = false, productData, questionsData }) => {

	useInjectReducer({ key: 'productQuestionsAndAnswers', reducer: productQuestionsAndAnswers });
	useInjectReducer({
		key: 'productSearchUgc_QuestionsAndAnswers',
		reducer: questionsAndAnswersSearchInstanceReducer
	});

	const dispatch = useDispatch();

	const {
		fetchStatus, questions, totalRecords, questionText, newQuestionModeEnabled, pageNumber
	} = useSelector(
		(state) => state.productQuestionsAndAnswers
	);

	// load
	useEffect(() => {

		if (!resetInitialState && fetchStatus === '') {

			dispatch(resetState({ questionsData }));

		}

	}, [ fetchStatus ]);

	// Reset
	useEffect(() => {

		if (resetInitialState) {

			dispatch(resetState({ questionsData }));

		}

	}, [ resetInitialState ]);

	const {
		searchTerm,
		searchResults,
		fetchStatus: searchFetchStatus,
		showMoreResultsFectchStatus,
		totalResultsCount,
		totalResultsLeft
	} = useSelector((state) => state.productSearchUgc_QuestionsAndAnswers);
	const [ showSubmitQuestionThankYou, setShowSubmitQuestionThankYou ] = useState(false);

	const handleClickShowMore = () => {

		dispatch(showMoreResults());
		utagLinkPDP('TurnTo')({
			eventAction: 'Q&A-More-Results',
			eventLabel: totalResultsLeft >= 10 ? '10' : totalResultsLeft
		});

	};
	const handleSubmitQuestionClick = () => {

		setShowSubmitQuestionThankYou(false);
		dispatch(enableNewQuestionMode());
		utagLinkPDP('TurnTo')({
			eventAction: 'Q&A-Click-New-Question'
		});

	};

	const handleThankyouQuestionSubmit = () => {

		setShowSubmitQuestionThankYou(true);
		utagLinkPDP('TurnTo')({
			eventAction: 'Q&A-Submit-Question'
		});

	};

	return (
		<div className="AppProductQuestionsAndAnswers">
			<header className="ProductQuestionsAndAnswersHeader">
				{newQuestionModeEnabled ? (
					<ProductQuestionsAndAnswersNewQuestion
						initialQuestionValue={questionText}
						productShortSku={productData.productShortSku}
						onSubmit={() => {

							handleThankyouQuestionSubmit();

						}}
						showLabel
					/>
				) : (
					<>
						<ProductQuestionsAndAnswersSearch showLabel />
					</>
				)}
				{questions.length > 0 && !isMobile && <ProductNewAdditionAndSorting />}
			</header>
			{/* eslint-disable-next-line max-len */}
			{showSubmitQuestionThankYou && (
				<ProductQuestionsAndAnswersThankYou message="Your question has been submitted. If you provided your email, you will receive notifications for 14 days. Otherwise, return to this page to see responses." />
			)}
			{!newQuestionModeEnabled && searchResults !== null && (
				<ProductSearchUGCResults
					instance="questionsAndAnswers"
					searchTerm={searchTerm}
					results={searchResults}
					isPendingResults={searchFetchStatus === 'pending'}
					isPendingShowMoreResults={showMoreResultsFectchStatus === 'pending'}
					productTitle={productData.productTitle}
					productCategory={productData.productCategory}
					totalResultsCount={totalResultsCount}
					totalResultsLeft={totalResultsLeft}
					handleClickShowMore={handleClickShowMore}
					handleSubmitQuestionClick={handleSubmitQuestionClick}
				/>
			)}
			{questions.length > 0 && isMobile && <ProductNewAdditionAndSorting />}
			<ProductQuestionsList questions={questions} pageNumber={pageNumber} />
			{totalRecords > 3 && <ProductQuestionsAndAnswersPaginationList />}
		</div>
	);

};
AppProductQuestionsAndAnswers.defaultProps = {
	productData: {
		productCategory: '',
		productShortSku: '',
		productTitle: ''
	}
};

AppProductQuestionsAndAnswers.propTypes = {
	productData: PropTypes.shape({
		productCategory: PropTypes.string,
		productShortSku: PropTypes.string,
		productTitle: PropTypes.string
	}),
	questionsData: PropTypes.object,
	resetInitialState: PropTypes.bool
};
export default AppProductQuestionsAndAnswers;
